import { forwardRef, Component as ReactComponent } from 'react';

import { ElementOrElementGetter, isTargetDescendant, Subtract } from '@optimus-web/common';

export interface ScrollAwayOptions {
    roots: ElementOrElementGetter[];
    handler: () => void;
}

export interface ScrollAwayProps {
    registerScrollAway: (options: ScrollAwayOptions) => void;
    deregisterScrollAway: () => void;
    pauseScrollAway: () => void;
    startScrollAway: () => void;
}

const withScrollAway = <P extends ScrollAwayProps, T extends React.Component<P>>(Component: React.ComponentType<P>) => {
    interface ForwardRefProps {
        forwardedRef?: React.Ref<T> | null;
    }

    type OriginalProps = Subtract<P, ScrollAwayProps>;
    type Props = OriginalProps;

    class ScrollAway extends ReactComponent<Props> {
        private registered = false;
        private paused = false;
        private roots?: ElementOrElementGetter[];
        private handler?: () => void;

        componentWillUnmount() {
            if (this.registered) {
                this.deregister();
            }
        }

        register = ({ roots, handler }: ScrollAwayOptions) => {
            if (!this.registered) {
                this.registered = true;
                this.roots = roots;
                this.handler = handler;

                window.addEventListener('scroll', this.handleWindowScroll, true);
            }
        };

        deregister = () => {
            if (this.registered) {
                this.registered = false;
                window.removeEventListener('scroll', this.handleWindowScroll, true);
            }
        };

        pause = () => {
            this.paused = true;
        };

        start = () => {
            this.paused = false;
        };

        handleWindowScroll = (e: Event) => {
            if (
                !this.paused &&
                this.roots &&
                this.handler &&
                !this.roots.some((r) => isTargetDescendant(e, typeof r === 'function' ? r() : r))
            ) {
                this.handler();
            }
        };

        render() {
            const { forwardedRef, ...other } = this.props as ForwardRefProps;

            const componentProps = {
                ref: forwardedRef,
                deregisterScrollAway: this.deregister,
                registerScrollAway: this.register,
                pauseScrollAway: this.pause,
                startScrollAway: this.start,
            };

            return <Component {...(other as P)} {...componentProps} />;
        }
    }

    return forwardRef<T, OriginalProps>((props: OriginalProps, ref?: React.Ref<T>) => (
        <ScrollAway {...props} forwardedRef={ref} />
    ));
};

export default withScrollAway;
